/**
 * communication.ts  —  Global Pinia store for unread badge counts.
 *
 * Owns one long-lived Gmail worker that starts at login and polls every
 * 60 s regardless of which page is open. Dashboard and nav badges
 * read gmailUnread / chatUnread from here.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import IntegrationsService from '@/core/services/IntegrationsService'
import type { GmailAccount } from '@/core/worker/gmail.worker'
import { ChatDatabaseService } from '@/services/ChatDatabaseService'
import { ChatServer } from '@/services/chat.server'
import { countChatUnread, openRequests } from '@/services/chatUnread'

/** Letzter bekannter Mail-Zaehlstand je Firma — damit der Header-Badge beim
 *  Neuladen sofort steht statt erst nach der Runde zu Google. */
const UNREAD_CACHE_KEY = (companyId: string) => `mg_gmail_unread_${companyId}`

function readCachedUnread(companyId: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(UNREAD_CACHE_KEY(companyId))
    const parsed = raw ? JSON.parse(raw) : null
    if (!parsed || typeof parsed !== 'object') return {}
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>)
        .map(([k, v]) => [k, Number(v)] as const)
        .filter(([, v]) => Number.isFinite(v))
    )
  } catch {
    return {}
  }
}

function writeCachedUnread(companyId: string, counts: Record<string, number>) {
  try {
    localStorage.setItem(UNREAD_CACHE_KEY(companyId), JSON.stringify(counts))
  } catch {
    // Privater Modus / volles Kontingent — der Badge steht dann eben erst nach
    // der ersten Antwort. Kein Grund, den Start zu stoeren.
  }
}

export const useCommunicationStore = defineStore('communication', () => {
  // ── State ──────────────────────────────────────────────────────────────
  const gmailUnreadByAccount = ref<Record<string, number>>({})
  const chatUnread           = ref(0)   // ungelesene Chat-Nachrichten
  const requestsOpen         = ref(0)   // offene Website-Anfragen
  const gmailAccounts        = ref<GmailAccount[]>([])
  const initialized          = ref(false)

  let worker: Worker | null = null
  let chatPollTimer: ReturnType<typeof setInterval> | null = null
  let activeCompanyId = ''

  // ── Computed ───────────────────────────────────────────────────────────
  const gmailUnread = computed(() =>
    Object.values(gmailUnreadByAccount.value).reduce((a, b) => a + b, 0)
  )
  // Der Header-Badge zeigt alles, was Aufmerksamkeit braucht — dieselben drei
  // Zahlen wie die Kommunikations-Übersicht (siehe services/chatUnread.ts).
  const totalUnread = computed(() =>
    gmailUnread.value + chatUnread.value + requestsOpen.value
  )

  // ── Gmail worker ───────────────────────────────────────────────────────
  function setGmailCount(entryId: string, unread: number) {
    gmailUnreadByAccount.value = {
      ...gmailUnreadByAccount.value,
      [entryId]: Math.max(0, Number(unread) || 0),
    }
    if (activeCompanyId) writeCachedUnread(activeCompanyId, gmailUnreadByAccount.value)
  }

  function startGmailWorker(accounts: GmailAccount[]) {
    if (worker) {
      worker.postMessage({ type: 'STOP_POLL' })
      worker.terminate()
    }
    worker = new Worker(
      new URL('@/core/worker/gmail.worker.ts', import.meta.url),
      { type: 'module' }
    )

    // Der Badge haengt ausschliesslich an UNREAD_COUNT — dem absoluten Stand aus
    // dem INBOX-Label. Vorher speiste ihn POLL_UPDATE, das aber ein DELTA ist
    // (nur die seit der letzten Runde hinzugekommenen Ids): bei 7 ungelesenen
    // Mails und einer neuen stand danach eine 1 im Badge, und ohne Neuzugang
    // meldet der Poll gar nichts — gelesene Mail liess die Zahl also nie sinken.
    worker.onmessage = (event: MessageEvent) => {
      const { type, ...data } = event.data
      if (type === 'UNREAD_COUNT') setGmailCount(data.entryId, data.unread)
    }

    const plain: GmailAccount[] = accounts.map(a => ({ ...a }))
    worker.postMessage({ type: 'INIT', accounts: plain })
    // Zaehlstand sofort holen: eine einzige Anfrage je Konto, damit die Zahl
    // gleich nach dem Anmelden am Icon steht. (Frueher lief hier FETCH_LIST —
    // 1 Listenabruf plus 50 Metadaten-Abrufe in 200-ms-Schritten je Konto,
    // also zweistellige Sekunden, bis der Badge zum ersten Mal etwas zeigte.)
    worker.postMessage({ type: 'FETCH_UNREAD' })
    worker.postMessage({ type: 'START_POLL', intervalMs: 60_000 })
  }

  /** Zaehlstand ausserhalb des Minutentakts nachziehen — nach dem Aufwachen und
   *  wenn eine Inbox-Ansicht Mails als gelesen markiert hat. Gebuendelt, weil
   *  beim Durchklicken einer Inbox sonst je geoeffneter Mail eine Anfrage
   *  entstuende. */
  let gmailRefreshTimer: ReturnType<typeof setTimeout> | null = null
  function refreshGmailUnread() {
    if (!worker) return
    if (gmailRefreshTimer) clearTimeout(gmailRefreshTimer)
    gmailRefreshTimer = setTimeout(() => {
      gmailRefreshTimer = null
      worker?.postMessage({ type: 'FETCH_UNREAD' })
    }, 1500)
  }

  function onGmailUnreadChanged() {
    refreshGmailUnread()
  }

  // ── Chat & Anfragen ────────────────────────────────────────────────────
  // Der Store hält die Konversationsliste selbst statt über die IndexedDB zu
  // summieren: die IDB ist ein wachsender Cache und behält Konversationen, die
  // der Server längst nicht mehr ausliefert (archiviert, Teilnahme beendet) —
  // die zählten sonst ewig als offene Anfrage weiter.
  const conversations = ref<any[]>([])

  function recount() {
    chatUnread.value   = countChatUnread(conversations.value)
    requestsOpen.value = openRequests(conversations.value).length
  }

  /** Vollständige Liste vom Chat-Server holen (Socket, HTTP-Fallback) und zählen.
   *  Bewusst der volle Abzug statt eines Deltas: nur so verschwinden Einträge,
   *  die der Server nicht mehr schickt. Es sind Metadaten, max. 100 Zeilen. */
  async function syncChatUnread(): Promise<boolean> {
    try {
      conversations.value = await ChatServer.fetchConversationsSnapshot()
      recount()
      return true
    } catch {
      // offline / Token noch nicht da → letzten Stand stehen lassen
      return false
    }
  }

  /** Erster Zählstand so früh wie möglich — bewusst UNABHÄNGIG vom Socket.
   *  `fetchConversationsSnapshot` fällt ohne Verbindung auf HTTP zurück, und
   *  apiFetch kümmert sich dort um Token-Refresh und 401-Retry. Der Badge darf
   *  nicht darauf warten, dass der Chat-Socket steht (der braucht am App-Start
   *  einen gültigen Token und kann Sekunden bis Minuten mit Reconnects
   *  verbringen) — genau daran erschien die Zahl erst nach einem Besuch im
   *  Kommunikationsbereich. Mit wachsendem Abstand nachfassen, bis es klappt. */
  async function initialSync() {
    const delays = [0, 400, 1200, 3000, 8000, 15_000]
    for (const wait of delays) {
      if (!initialized.value) return
      if (wait) await new Promise(r => setTimeout(r, wait))
      if (await syncChatUnread()) return
    }
    console.warn('[CommunicationStore] Chat-Zählstand konnte initial nicht geladen werden')
  }

  /** Sofortiges lokales Nachziehen ohne Server-Runde: nach markRead ist das
   *  Read-Receipt evtl. noch nicht geschrieben, ein Fetch lieferte dann den
   *  alten Stand zurück. Das Event trägt, was sich geändert hat. */
  function onConvChanged(e: Event) {
    const d: any = (e as CustomEvent).detail || {}
    if (!d.conversationId) { syncChatUnread(); return }
    const idx = conversations.value.findIndex((c: any) => c.id === d.conversationId)
    if (idx < 0) { syncChatUnread(); return }
    const conv = { ...conversations.value[idx] }
    if (d.read) conv.unread = 0
    if ('status' in d) {
      let opts: any = {}
      try {
        const raw = conv.options
        opts = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : {}
      } catch { opts = {} }
      if (d.status === 'done') opts.status = 'done'
      else delete opts.status
      conv.options = JSON.stringify(opts)
    }
    conversations.value.splice(idx, 1, conv)
    recount()
  }

  // Eigene Socket-Verbindung ab Login — die Chat-Views verbinden erst beim
  // Öffnen; ohne Verbindung kämen weder chat:notify noch aktuelle Zähler an.
  // Beim App-Start kann der Studio-Token noch fehlen (der Worker schreibt ihn
  // asynchron in die IDB), deshalb mit Geduld nachfassen.
  async function connectChat() {
    // Kurz anfangen, dann strecken: der Token ist meist nach wenigen hundert
    // Millisekunden da; 3-s-Schritte verschenkten unnötig Zeit.
    let wait = 300
    for (let i = 0; i < 20 && initialized.value; i++) {
      const jwt = await ChatDatabaseService.getStudioToken().catch(() => '')
      if (jwt) {
        ChatServer.connect(jwt)
        // Auch bei jedem Reconnect syncen — holt nach, was offline verpasst wurde
        ChatServer.onConnected(() => { syncChatUnread() })
        ChatServer.onNotify(() => { syncChatUnread() })
        // Neue Anfrage/Konversation kommt als eigenes Event (ohne Nachricht)
        ChatServer.onConversationNew(() => { syncChatUnread() })
        return
      }
      await new Promise(r => setTimeout(r, wait))
      wait = Math.min(wait * 2, 5000)
    }
    console.warn('[CommunicationStore] kein Studio-Token — Chat-Live-Updates aus')
  }

  /** Nach dem Aufwachen (iOS friert Seite und Socket ein) sofort nachziehen,
   *  statt bis zum nächsten Fallback-Poll eine veraltete Zahl zu zeigen. */
  function onVisible() {
    if (document.visibilityState !== 'visible') return
    syncChatUnread()
    refreshGmailUnread()
  }

  // ── Public: init on login ──────────────────────────────────────────────
  async function initGmail(companyId: string) {
    activeCompanyId = companyId
    // Letzter bekannter Stand zuerst — synchron aus dem localStorage, damit am
    // Icon sofort eine Zahl steht statt einer Sekunde Leere. Die Runde zu
    // Google ueberschreibt sie gleich darauf.
    gmailUnreadByAccount.value = readCachedUnread(companyId)
    try {
      const raw = await IntegrationsService.allGmail(companyId)
      const accounts: GmailAccount[] = raw
        .filter((r: any) => r.config?.refreshToken)
        .map((r: any) => ({
          entryId:      String(r.key ?? ''),
          email:        String(r.config?.email       ?? ''),
          displayName:  String(r.config?.displayName ?? ''),
          refreshToken: String(r.config?.refreshToken ?? ''),
        }))
      gmailAccounts.value = accounts
      // Konten, die es nicht mehr gibt, aus dem zwischengespeicherten Stand
      // werfen — sonst zaehlte ein getrenntes Postfach ewig weiter mit.
      const known = new Set(accounts.map(a => a.entryId))
      gmailUnreadByAccount.value = Object.fromEntries(
        Object.entries(gmailUnreadByAccount.value).filter(([id]) => known.has(id))
      )
      writeCachedUnread(companyId, gmailUnreadByAccount.value)
      if (accounts.length) {
        startGmailWorker(accounts)
      } else if (worker) {
        // Firmenwechsel auf eine Firma ohne Postfach: der Worker der alten
        // Firma pollte sonst weiter und schriebe deren Zahlen in den Badge.
        worker.postMessage({ type: 'STOP_POLL' })
        worker.terminate()
        worker = null
      }
    } catch (e) {
      console.warn('[CommunicationStore] Gmail init failed', e)
    }
  }

  async function startPolling(companyId: string) {
    if (initialized.value) return
    initialized.value = true

    // Reihenfolge ist Absicht: der Chat-Zählstand zuerst und ohne `await` auf
    // irgendetwas Langsames. Vorher lief Gmail zuerst und der Chat-Teil hing
    // hinter zwei awaits (Integrations-Lookup, IndexedDB-open) — auf dem
    // Dashboard blieb der Badge deshalb leer.
    ChatDatabaseService.open()   // im Hintergrund; getDB() öffnet ohnehin faul
    initialSync()                // erster Zählstand, notfalls über HTTP
    connectChat()                // Live-Updates, sobald der Socket steht

    // Poll nur als Fallback — primär kommt chat:notify über den Socket
    chatPollTimer = setInterval(syncChatUnread, 60_000)
    document.addEventListener('visibilitychange', onVisible)

    // Sofort-Update bei lokalen Änderungen (gelesen / erledigt). Bewusst kein
    // ChatServer.onMessage hier: die Chat-Views räumen 'chat:message:new'-
    // Listener bei Unmount pauschal ab (socket.off ohne Handler) und würden
    // diesen Listener mit entfernen.
    window.addEventListener('chat:unread-changed', onConvChanged)
    // Die Inbox-Ansichten fahren einen eigenen Worker; markieren sie dort eine
    // Mail als gelesen, erfaehrt dieser Store es nur ueber das Event.
    window.addEventListener('gmail:unread-changed', onGmailUnreadChanged)

    await initGmail(companyId)
  }

  function stopPolling() {
    if (worker) {
      worker.postMessage({ type: 'STOP_POLL' })
      worker.terminate()
      worker = null
    }
    if (chatPollTimer) {
      clearInterval(chatPollTimer)
      chatPollTimer = null
    }
    if (gmailRefreshTimer) {
      clearTimeout(gmailRefreshTimer)
      gmailRefreshTimer = null
    }
    window.removeEventListener('chat:unread-changed', onConvChanged)
    window.removeEventListener('gmail:unread-changed', onGmailUnreadChanged)
    document.removeEventListener('visibilitychange', onVisible)
    ChatServer.off('chat:notify')
    // Socket schließen — ein Re-Login verbindet frisch (connect() legt bei
    // socket !== null sonst keine neue Verbindung mit neuem Token an)
    ChatServer.disconnect()
    conversations.value = []
    chatUnread.value = 0
    requestsOpen.value = 0
    gmailUnreadByAccount.value = {}
    activeCompanyId = ''
    initialized.value = false
  }

  /** Firmenwechsel: nur die Gmail-Konten hängen an der Company. Der Chat-Socket
   *  ist nutzerbezogen (JWT) und bleibt bestehen — ihn zu trennen würde eine
   *  offene Chat-Ansicht mitreißen, die ihre Handler am alten Socket hat. */
  async function restartPolling(companyId: string) {
    if (!initialized.value) return startPolling(companyId)
    await initGmail(companyId)
    await syncChatUnread()
  }

  return {
    gmailUnread,
    chatUnread,
    requestsOpen,
    totalUnread,
    gmailAccounts,
    gmailUnreadByAccount,
    conversations,
    initialized,
    startPolling,
    stopPolling,
    restartPolling,
    syncChatUnread,
    refreshGmailUnread,
  }
})
